import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { billingApi } from '@/api/ops';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { formatWhen } from '@/pages/AccountsPage';

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

/**
 * External accounts (2026-09-30): customers that use our SMS / WhatsApp and payments APIs from their own system — an
 * online-ordering platform, say — without the CRM (D-CP7). Listed live from billing on the selected environment (CP2);
 * each opens its tenant page, where its API keys, webhooks, SMS account and billing live.
 */
export function ExternalAccountsPage() {
  const { env, current } = useSelectedEnvironment();
  const [status, setStatus] = useState('');
  const canCreate = hasPermission(PERMISSIONS.tenantProvision);

  const accounts = useInfiniteQuery({
    queryKey: ['ops', 'billing', env, 'accounts', 'external', status],
    queryFn: ({ pageParam }) => billingApi.accounts(env!, { kind: 'external', status: status || undefined, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!env,
    ...LIVE,
  });
  const rows = accounts.data?.pages.flatMap((p) => p.accounts) ?? [];
  const tenantPath = (tenantId: string, tab: string) => `/ops/tenants/${encodeURIComponent(env ?? '')}/${tenantId}?tab=${tab}`;

  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="External accounts">
      <PageHeader
        title="External accounts"
        subtitle="Systems that use our SMS / WhatsApp and payments APIs without the CRM — for example an online-ordering platform."
        actions={canCreate && <Link to="/ops/provisioning/new?kind=external" className="btn btn-primary">Add an external account</Link>}
      />

      <div className="filterbar ops-filters">
        <Select label="Status" inline value={status} onChange={setStatus} placeholder="Any status" searchable={false} options={STATUSES} />
        <span className="t-caption muted">On {current?.label ?? env ?? '—'} — switch environments in the top bar.</span>
      </div>

      <Section title="Accounts" meta={env ? `live from billing · ${rows.length}${accounts.hasNextPage ? '+' : ''} shown` : undefined} flush>
        {!env ? (
          <div className="empty-state t-body">Choose an environment in the top bar.</div>
        ) : (
          <QueryState query={{ ...accounts, data: accounts.data ? rows : undefined }} service="billing">
            {(list) => list.length === 0 ? (
              <div className="empty-state t-body">
                No external accounts {status ? `with status “${status}” ` : ''}on this environment yet.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Account</th><th>Status</th><th>Created</th><th /></tr>
                  </thead>
                  <tbody>
                    {list.map((a) => (
                      <tr key={a.tenant_id}>
                        <td>
                          <Link to={tenantPath(a.tenant_id, 'overview')} style={{ fontWeight: 600 }}>{a.name || '(unnamed)'}</Link>
                          <div className="t-caption muted mono">{a.tenant_id}</div>
                        </td>
                        <td><StatusChip status={a.status} /></td>
                        <td className="t-caption" title={formatTimestamp(a.created_at)}>{formatWhen(a.created_at)}</td>
                        <td>
                          <span className="row" style={{ justifyContent: 'flex-end' }}>
                            <Link className="btn btn-ghost btn-sm" to={tenantPath(a.tenant_id, 'integrations')}>API keys & webhooks</Link>
                            <Link className="btn btn-ghost btn-sm" to={tenantPath(a.tenant_id, 'messaging')}>SMS account</Link>
                            <Link className="btn btn-ghost btn-sm" to={tenantPath(a.tenant_id, 'billing')}>Billing</Link>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </QueryState>
        )}
        <LoadMore hasMore={!!accounts.hasNextPage} loading={accounts.isFetchingNextPage} onLoad={() => accounts.fetchNextPage()} />
      </Section>
    </RequirePermission>
  );
}
